// @flow
import type { Resource } from 'esm-web-pro/containers/map/types';
import * as React from 'react';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import type { LatLng } from 'react-leaflet/lib/types';
import ShowContentControl from 'esm-web-pro/containers/pages/network/map-controls/ShowContentControl';
import ShowMapObjectsControl from 'esm-web-pro/containers/pages/network/map-controls/ShowMapObjectsControl';
import MeasureControl from 'esm-web-pro/containers/pages/network/map-controls/MeasureControl';
import PolygonControl from 'esm-web-pro/containers/pages/network/map-controls/PolygonControl';
import CoordinateSystemControl from 'esm-web-pro/containers/pages/network/map-controls/CoordinateSystemControl';
import ShowLines from 'esm-web-pro/assets/images/map/showLines.svg';
import HideLines from 'esm-web-pro/assets/images/map/hideLines.svg';
import ShowPoints from 'esm-web-pro/assets/images/map/showMarkers.svg';
import HidePoints from 'esm-web-pro/assets/images/map/hideMarkers.svg';
import ShowRegions from 'esm-web-pro/assets/images/map/showPolygons.svg';
import HideRegions from 'esm-web-pro/assets/images/map/hidePolygons.svg';
import GroupObjectsControl from 'esm-web-pro/containers/pages/network/map-controls/GroupObjectsControl';
import FilterObjectsControl from 'esm-web-pro/containers/map/controls/FilterObjectsControl';
import { withRouter } from 'react-router-dom';

type Props = {
    forwarded: Object,
    zoom: number,
    center: [number, number],
    // eslint-disable-next-line react/require-default-props
    setPosition: (center: LatLng, zoom: number) => {},
    resources: Array<Resource>,
    bounds: Object,
    creator: React.ElementType,
    allowCreate: boolean,
    allowMeasure: boolean,
    showMapObjects: Object,
    toggleShowMapObjects: Function,
    currentMap: Object,
    location: Object,
}

export const CONTENT_TOOLTIP_PANE_NAME = 'content-tooltip-pane';


class NetworkMapEditor extends React.Component<Props> {
    drawFeature = (result: Object, feature: Object, layer: Object, drawing: boolean, getFeatureTitle: Function) => {
        let tooltipContent;

        if (result.resource.tooltipContent) {
            // eslint-disable-next-line prefer-destructuring
            tooltipContent = result.resource.tooltipContent(feature);
        } else {
            tooltipContent = getFeatureTitle(result.resource, feature);
        }

        if (typeof result.resource.additionalOptions === typeof Function) {
            layer.options = {
                ...layer.options,
                ...result.resource.additionalOptions(feature, layer),
            };
        }

        if (tooltipContent) {
            layer.bindTooltip(tooltipContent, { className: 'tooltip' });
        }
    };

    updateFeature = (result: Object, feature: Object, layer: Object, drawing: boolean, getFeatureTitle: Function) => {
        if (typeof result.resource.additionalOptions === typeof Function && !drawing) {
            const newStyle = {
                ...layer.options,
                ...result.resource.additionalOptions(feature),
            };

            switch (result.resource.type) {
            case 'point':
                layer.setIcon(newStyle.icon);
                break;
            default:
                if ('setStyle' in layer) {
                    layer.setStyle(newStyle);
                }
            }
        }

        let tooltipContent;

        if (result.resource.tooltipContent) {
            // eslint-disable-next-line prefer-destructuring
            tooltipContent = result.resource.tooltipContent(feature);
        } else {
            tooltipContent = getFeatureTitle(result.resource, feature);
        }

        if (tooltipContent) {
            if (layer.getTooltip() && layer.isTooltipOpen()) {
                layer.closeTooltip();
            }
            layer.bindTooltip(tooltipContent, { className: 'tooltip' });
        } else if (!tooltipContent && layer.getTooltip()) {
            layer.unbindTooltip();
        }
    };

    render() {
        const {
            forwarded, zoom, center, setPosition, resources, bounds, creator, allowCreate, currentMap, allowMeasure,
            showMapObjects, toggleShowMapObjects, location: { pathname },
        } = this.props;

        return (
            <BaseEditor
                ref={forwarded}
                zoom={zoom}
                center={center}
                setPosition={setPosition}
                resources={resources}
                bounds={bounds}
                currentMap={currentMap}
                drawFeature={this.drawFeature}
                creator={creator}
                allowCreate={allowCreate}
                allowMeasure={allowMeasure}
                updateFeature={this.updateFeature}
                showMapObjects={showMapObjects}
                toggleShowMapObjects={toggleShowMapObjects}
                controls={[
                    {
                        component: MeasureControl,
                        props: {
                            show: currentMap.is_draft,
                        },
                        position: 'topright',
                        key: 'networks-map-measure-control',
                    },
                    {
                        component: PolygonControl,
                        props: {
                            show: currentMap.is_draft,
                        },
                        position: 'topright',
                        key: 'networks-map-polygon-control',
                    },
                    {
                        component: CoordinateSystemControl,
                        props: {},
                        position: 'topleft',
                        key: 'networks-map-coordinates-control',
                    },
                    {
                        component: ShowContentControl,
                        props: {},
                        position: 'topleft',
                        key: 'networks-map-showcontent-control',
                    },
                    {
                        component: ShowMapObjectsControl,
                        props: {
                            toggle: toggleShowMapObjects,
                            argument: 'regions',
                            show: showMapObjects.regions.show,
                            showIcon: ShowRegions,
                            hideIcon: HideRegions,
                        },
                        position: 'topleft',
                        key: 'networks-map-show-regions-control',
                    },
                    {
                        component: ShowMapObjectsControl,
                        props: {
                            toggle: toggleShowMapObjects,
                            argument: 'points',
                            show: showMapObjects.points.show,
                            showIcon: ShowPoints,
                            hideIcon: HidePoints,
                        },
                        position: 'topleft',
                        key: 'networks-map-show-points-control',
                    },
                    {
                        component: ShowMapObjectsControl,
                        props: {
                            toggle: toggleShowMapObjects,
                            argument: 'lines',
                            show: showMapObjects.lines.show,
                            showIcon: ShowLines,
                            hideIcon: HideLines,
                        },
                        position: 'topleft',
                        key: 'networks-map-show-lines-control',
                    },
                    {
                        component: GroupObjectsControl,
                        props: {},
                        position: 'topright',
                        key: 'networks-map-GroupObjects-control',
                    },
                ]}
                panes={[
                    {
                        name: 'content-tooltip-pane',
                    },
                ]}
            >
                <h3>Network</h3>
            </BaseEditor>
        );
    }
}

export default withRouter(NetworkMapEditor);
